import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  BarChart3,
  Download,
  Building,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Home,
  Users,
  FileText,
  Calendar,
  PieChart,
  Eye
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#4f46e5', '#7c3aed', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];

/*
 * Altam™ Operating Services
 * Board Portal Dashboard - Read-Only Access
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function BoardDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [financialSummary, setFinancialSummary] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState('overview');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchBoardData();
  }, []);

  const fetchBoardData = async () => {
    try {
      const [statsRes, summaryRes, propsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/transactions/summary?months=12`),
        axios.get(`${API}/corporations`)
      ]);

      setStats(statsRes.data);
      setFinancialSummary(summaryRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      console.error('Failed to fetch board data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const downloadReport = async (reportType) => {
    setDownloading(true);
    try {
      // Generate report data for download
      const reportData = generateReportData(reportType);
      const blob = new Blob([reportData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `altam_${reportType}_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
    } finally {
      setDownloading(false);
    }
  };

  const generateReportData = (reportType) => {
    const now = new Date().toLocaleDateString();
    
    if (reportType === 'financial') {
      let csv = 'Altam Board Financial Report\n';
      csv += `Generated: ${now}\n\n`;
      csv += 'Month,Income,Expenses,Net Income\n';
      financialSummary.forEach(row => {
        csv += `${row.month},${row.income},${row.expenses},${row.income - row.expenses}\n`;
      });
      csv += `\nTotal Properties,${stats?.total_properties || 0}\n`;
      csv += `Total Units,${stats?.total_units || 0}\n`;
      csv += `Occupied Units,${stats?.occupied_units || 0}\n`;
      csv += `Vacancy Rate,${stats?.vacancy_rate || 0}%\n`;
      return csv;
    }
    
    if (reportType === 'properties') {
      let csv = 'Altam Property Overview Report\n';
      csv += `Generated: ${now}\n\n`;
      csv += 'Property Name,Type,Address,City,Units,Status\n';
      properties.forEach(prop => {
        csv += `"${prop.name}",${prop.property_type},${prop.address},${prop.city},${prop.unit_count},${prop.status}\n`;
      });
      return csv;
    }
    
    // Overview report
    let csv = 'Altam Board Overview Report\n';
    csv += `Generated: ${now}\n\n`;
    csv += 'Metric,Value\n';
    csv += `Total Properties,${stats?.total_properties || 0}\n`;
    csv += `Total Units,${stats?.total_units || 0}\n`;
    csv += `Occupied Units,${stats?.occupied_units || 0}\n`;
    csv += `Vacancy Rate,${stats?.vacancy_rate || 0}%\n`;
    csv += `Monthly Income,${stats?.total_income || 0}\n`;
    csv += `Monthly Expenses,${stats?.total_expenses || 0}\n`;
    csv += `Open Maintenance,${stats?.open_maintenance || 0}\n`;
    csv += `Pending Invoices,${stats?.pending_invoices || 0}\n`;
    return csv;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Calculate totals
  const totalIncome = financialSummary.reduce((sum, m) => sum + (m.income || 0), 0);
  const totalExpenses = financialSummary.reduce((sum, m) => sum + (m.expenses || 0), 0);
  const netIncome = totalIncome - totalExpenses;
  const occupancyRate = stats?.total_units ? ((stats.occupied_units / stats.total_units) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6" data-testid="board-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Board Member Dashboard</h1>
            <p className="text-indigo-100 mt-1">
              Read-only access to property performance and financial reports
            </p>
            {stats?.assigned_property_names && stats.assigned_property_names.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <Building className="w-4 h-4 text-indigo-200" />
                <span className="text-indigo-100">Assigned Properties:</span>
                <span className="font-medium">{stats.assigned_property_names.join(', ')}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-lg">
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">View Only</span>
          </div>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'financial', label: 'Financial Summary', icon: DollarSign },
          { id: 'properties', label: 'Property Details', icon: Building }
        ].map((report) => (
          <button
            key={report.id}
            onClick={() => setSelectedReport(report.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedReport === report.id
                ? 'bg-indigo-600 text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <report.icon className="w-4 h-4" />
            {report.label}
          </button>
        ))}
        
        {/* Download Button */}
        <button
          onClick={() => downloadReport(selectedReport)}
          disabled={downloading}
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          data-testid="download-report-btn"
        >
          <Download className={`w-4 h-4 ${downloading ? 'animate-bounce' : ''}`} />
          {downloading ? 'Downloading...' : 'Download Report'}
        </button>
      </div>

      {/* Overview Report */}
      {selectedReport === 'overview' && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-100 p-3 rounded-xl">
                  <Building className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_properties || 0}</p>
                  <p className="text-sm text-slate-500">Properties</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="bg-violet-100 p-3 rounded-xl">
                  <Home className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_units || 0}</p>
                  <p className="text-sm text-slate-500">Total Units</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 p-3 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{occupancyRate}%</p>
                  <p className="text-sm text-slate-500">Occupancy Rate</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 p-3 rounded-xl">
                  <FileText className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats?.pending_invoices || 0}</p>
                  <p className="text-sm text-slate-500">Pending Invoices</p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Financial Trend */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">12-Month Financial Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={financialSummary}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={2} name="Income" />
                  <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} name="Expenses" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Occupancy Chart */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Portfolio Occupancy</h3>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPie>
                  <Pie
                    data={[
                      { name: 'Occupied', value: stats?.occupied_units || 0 },
                      { name: 'Vacant', value: (stats?.total_units || 0) - (stats?.occupied_units || 0) }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#4f46e5" />
                    <Cell fill="#e2e8f0" />
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Financial Summary Report */}
      {selectedReport === 'financial' && (
        <>
          {/* Financial Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">YTD Income</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalIncome)}</p>
                </div>
                <div className="bg-emerald-100 p-3 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">YTD Expenses</p>
                  <p className="text-2xl font-bold text-rose-600">{formatCurrency(totalExpenses)}</p>
                </div>
                <div className="bg-rose-100 p-3 rounded-xl">
                  <TrendingDown className="w-6 h-6 text-rose-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Net Income</p>
                  <p className={`text-2xl font-bold ${netIncome >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatCurrency(netIncome)}
                  </p>
                </div>
                <div className={`${netIncome >= 0 ? 'bg-emerald-100' : 'bg-rose-100'} p-3 rounded-xl`}>
                  <DollarSign className={`w-6 h-6 ${netIncome >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Breakdown */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Monthly Income vs Expenses</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={financialSummary}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v/1000}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="income" fill="#10b981" name="Income" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Financial Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Monthly Financial Data</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Month</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Income</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Expenses</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Net</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {financialSummary.map((row, idx) => {
                    const net = (row.income || 0) - (row.expenses || 0);
                    const margin = row.income ? ((net / row.income) * 100).toFixed(1) : 0;
                    return (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{row.month}</td>
                        <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(row.income)}</td>
                        <td className="px-4 py-3 text-right text-rose-600">{formatCurrency(row.expenses)}</td>
                        <td className={`px-4 py-3 text-right font-medium ${net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {formatCurrency(net)}
                        </td>
                        <td className={`px-4 py-3 text-right ${margin >= 0 ? 'text-slate-600' : 'text-rose-600'}`}>
                          {margin}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Properties Report */}
      {selectedReport === 'properties' && (
        <>
          {/* Property Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {properties.map((prop) => (
              <div key={prop.id} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-indigo-100 p-2 rounded-lg">
                    <Building className="w-5 h-5 text-indigo-600" />
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    prop.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                  }`}>
                    {prop.status}
                  </span>
                </div>
                <h4 className="font-semibold text-slate-900">{prop.name}</h4>
                <p className="text-sm text-slate-500 capitalize">{prop.property_type}</p>
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                  <p className="text-sm text-slate-600">{prop.address}</p>
                  <p className="text-sm text-slate-500">{prop.city}, {prop.province}</p>
                  <p className="text-sm font-medium text-indigo-600">{prop.unit_count} Units</p>
                </div>
              </div>
            ))}
          </div>

          {/* Properties Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Property Portfolio Details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Property</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Address</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Units</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Contact</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {properties.map((prop) => (
                    <tr key={prop.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{prop.name}</div>
                        <div className="text-xs text-slate-500">{prop.legal_name}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 capitalize">{prop.property_type}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-900">{prop.address}</div>
                        <div className="text-xs text-slate-500">{prop.city}, {prop.province} {prop.postal_code}</div>
                      </td>
                      <td className="px-4 py-3 text-center font-medium text-indigo-600">{prop.unit_count}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-600">{prop.phone}</div>
                        <div className="text-xs text-slate-500">{prop.email}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          prop.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {prop.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Read-Only Notice */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3">
          <Eye className="w-5 h-5 text-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-700">Read-Only Access</p>
            <p className="text-xs text-slate-500">
              This portal provides view-only access to board reports. For questions or to request changes, 
              please contact the property management team at <strong>admin@altam.io</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
